import { useEffect, useRef } from 'react'
import { useSyncStreamingState } from '@renderer/chat'
import { removeCompletedTurn, useIsTurnLive, useTurnStatus } from '@renderer/chat/store/turn-status'
import { toastManager } from '@renderer/overlay/Toaster/toast-store'

interface ChatStreamingLifecycleParams {
  /** The chat the surface is showing, pre-temp→real resolution. */
  chatId: string | undefined
  /**
   * The same chat, resolved — what the streaming store keys on. Surfaces with
   * no temp-id flow (the action chat drawer) pass `chatId` again.
   */
  resolvedChatId: string | undefined
  /** The Chat View's new-chat mode — suppresses the verify until an id exists. */
  isNew?: boolean
  /**
   * A fresh local submit owns this chat's stream. Suppresses the verify +
   * re-subscribe — re-subscribing would open a competing SSE stream that
   * clobbers the in-flight reply.
   */
  isFreshSubmit?: boolean
  /**
   * Called when the local stream goes idle while `isFreshSubmit` is still set.
   * For callers whose flag persists (the Chat View's lives in the durable view
   * context) and must be cleared at its source — without it the flag would
   * outlive the submit, wedging the verify off for the whole session (and
   * across a hard reload) while the backend may still be streaming. Callers
   * whose flag self-derives from in-memory state (the drawer reads the pending
   * edit state) omit it.
   */
  onFreshSubmitSettled?: () => void
}

/**
 * A conversation surface's relationship with the live stream: verifying
 * backend state on open, suppressing that verification while a local submit
 * owns the stream, and clearing the completion toast for a chat the user is
 * already watching. Shared by the Chat View and the Action View's chat drawer.
 */
export function useChatStreamingLifecycle({
  chatId,
  resolvedChatId,
  isNew = false,
  isFreshSubmit = false,
  onFreshSubmitSettled,
}: ChatStreamingLifecycleParams): void {
  // When a chat completes while the user is viewing it, remove it from the
  // completed list so no toast fires later when the sidepanel closes
  // (HIG-5311). Track the chat the surface is showing (resolvedChatId), not
  // the global activeChatId — with parallel chats, activeChatId follows the
  // latest stream while the surface can stay on an earlier chat. Guard on the
  // previous chat id so switching from a streaming chat to an idle one doesn't
  // read as a completion.
  const isStreaming = useIsTurnLive(resolvedChatId)
  const wasStreamingRef = useRef(isStreaming)
  const wasStreamingChatIdRef = useRef(resolvedChatId)
  useEffect(() => {
    const sameChat = wasStreamingChatIdRef.current === resolvedChatId
    if (sameChat && wasStreamingRef.current && !isStreaming && resolvedChatId) {
      removeCompletedTurn(resolvedChatId)
      toastManager.dismiss(`chat-complete-${resolvedChatId}`)
    }
    wasStreamingRef.current = isStreaming
    wasStreamingChatIdRef.current = resolvedChatId
  }, [isStreaming, resolvedChatId])

  // Verify streaming state with the backend when the surface opens (skipped
  // while isNew / isFreshSubmit — see the params' docs).
  const { unsubscribe } = useSyncStreamingState(chatId, isNew, isFreshSubmit)

  // Hand a persistent fresh-submit flag back to its owner once the local
  // stream is idle. The active-streaming set is in-memory and resets on
  // reload, so this fires again after a hard refresh — letting the owner
  // re-enable the verify and re-subscribe to a still-live backend stream.
  //
  // `draining` keeps the flag: liveness drops between a finished turn and the
  // queued follow-up that replaces it, and settling in that window would
  // re-subscribe the verify against a POST that is about to open its own
  // stream. The local submit still owns the chat until its queue is empty.
  const turnStatus = useTurnStatus(resolvedChatId)
  useEffect(() => {
    if (!isFreshSubmit || isStreaming || turnStatus === 'draining') return
    onFreshSubmitSettled?.()
  }, [isFreshSubmit, isStreaming, turnStatus, onFreshSubmitSettled])

  // Clean up the subscription when the surface unmounts
  useEffect(() => {
    return () => {
      unsubscribe()
    }
  }, [unsubscribe])
}
